<template>
  <!-- The still <img> is ALWAYS the background layer; the <video> is layered on
       top only on the focused card. Keeping the image mounted through the
       side↔centre transition means there is never a frame where neither element
       has painted — which is what let the card's base colour flash yellow when
       a card swapped its <img> out for a <video>. The still is the video's own
       first frame, so the hand-off is seamless. -->
  <div class="absolute inset-0 h-full w-full">
    <img
      v-if="poster"
      :src="poster"
      class="absolute inset-0 h-full w-full object-cover"
      alt=""
      aria-hidden="true"
      draggable="false"
    />
    <!-- Focused card only. Its poster is the same still, so it paints instantly
         before playback starts even if the image below were ever absent. A side
         card has no <video> at all, so it needs no video decoder — the whole
         reason it can never revert to the base colour. -->
    <video
      v-if="playing"
      ref="video"
      :src="src"
      :poster="poster || undefined"
      class="absolute inset-0 h-full w-full object-cover"
      loop
      muted
      playsinline
      preload="auto"
      aria-hidden="true"
      disablepictureinpicture
      disableremoteplayback
      @contextmenu.prevent
    ></video>
  </div>
</template>

<script>
export default {
  name: 'CardVideoBackground',
  props: {
    src: {
      type: String,
      default: '',
    },
    // First-frame still. The always-on <img> below and the focused card's
    // <video> poster both use it.
    poster: {
      type: String,
      default: '',
    },
    // Only the focused card plays; every other card shows the still <img> alone.
    playing: {
      type: Boolean,
      default: true,
    },
  },
  watch: {
    src: 'restart',
    playing(now) {
      // Pre-flush watcher: on true→false the <video> is still in the DOM, so
      // release its decoder before v-if removes it. On false→true the <video>
      // has yet to mount, so start it after the DOM updates.
      if (now) this.$nextTick(this.playVideo);
      else this.releaseVideo();
    },
  },
  mounted() {
    if (this.playing) this.playVideo();
  },
  beforeUnmount() {
    this.releaseVideo();
  },
  methods: {
    playVideo() {
      const video = this.$refs.video;
      if (!video || !this.src) return;
      // iOS only autoplays inline video that is muted; Vue does not reliably
      // reflect the `muted` attribute to the DOM property, so set it directly.
      video.muted = true;
      // The `autoplay` attribute is intentionally omitted — kick playback off
      // explicitly, swallowing the rejection when the element is not allowed to
      // play (e.g. a backgrounded tab).
      const started = video.play();
      if (started?.catch) started.catch(() => {});
    },
    restart() {
      const video = this.$refs.video;
      if (video) video.currentTime = 0;
      this.$nextTick(this.playVideo);
    },
    releaseVideo() {
      const video = this.$refs.video;
      if (!video) return;
      // Free the media resource, not just playback. Detaching the source and
      // calling load() releases the decoder immediately so it never leaks.
      video.pause();
      video.removeAttribute('src');
      video.load();
    },
  },
};
</script>
